import React, { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { ConsolidatedCoinData, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { ICON_TYPES } from "@shared/common/constants";
import IconWithFallback from "Global/IconWithFallback";
import { DISPLAY_CURRENCIES } from "common/constants";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import { OverviewZoneTitle } from "common/NewLook/OverviewPanel.tsx";
import { useAssetsData } from "features/portfolio/common/hooks";
import {
  formatMoney,
  formatSignedMoney,
  formatSignedPercent,
} from "features/portfolio/NewLook/format.ts";
import { getPositionFigures } from "features/portfolio/NewLook/positionFigures.ts";
import { RootState } from "app/store";

const TOP_MOVERS = 4;
/** A narrow zone (smaller laptops): the movers keep the money, not the %. */
const NARROW_ZONE = "@container (max-width: 640px)";

interface NewLookPositionsPnlProps {
  dataSelector: (state: RootState) => ConsolidatedCoinData[];
  loading?: boolean;
}

/**
 * The positions page's overview zone: the open P&L across positions, the
 * margin split between longs and shorts, and the positions moving the most.
 * Read from the positions themselves (there is no P&L history to chart).
 */
const NewLookPositionsPnl = ({
  dataSelector,
  loading,
}: NewLookPositionsPnlProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const tokens = getNewLookTokens(useTheme());
  const isBtc = useSelector(selectDisplayCurrency) === DISPLAY_CURRENCIES.BTC;
  const symbol = isBtc ? "₿" : "$";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const { filteredCoinsData: positions } = useAssetsData({
    dataSelector,
    isPositions: true,
  });

  const summary = useMemo(() => {
    let pnl = 0;
    let margin = 0;
    let longMargin = 0;
    let shortMargin = 0;
    let longCount = 0;
    const rows = positions.map((position) => {
      const figures = getPositionFigures(position, isBtc);
      pnl += figures.pnl;
      margin += figures.margin;
      if (position.is_short) shortMargin += figures.margin;
      else {
        longMargin += figures.margin;
        longCount += 1;
      }
      return { position, ...figures };
    });
    const movers = rows
      .filter((row) => row.pnl)
      .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
      .slice(0, TOP_MOVERS);
    return {
      pnl,
      roi: margin ? (pnl / margin) * 100 : 0,
      longMargin,
      shortMargin,
      longCount,
      shortCount: positions.length - longCount,
      longShare: margin ? longMargin / margin : 0,
      movers,
    };
  }, [positions, isBtc]);

  const pending = !!loading && !positions.length;
  const color = directionColor(tokens, summary.pnl);
  const share = (value: number) => `${Math.round(value * 100)}%`;

  return (
    <Wrapper data-testid="positions-pnl">
      <OverviewZoneTitle>
        {t("phone.unrealized_pnl")}
        {!pending && (
          <Count>
            {t("overview.open_positions", { count: positions.length })}
          </Count>
        )}
      </OverviewZoneTitle>
      <Body>
        <Lead>
          {pending ? (
            <Skeleton width={180} height={40} />
          ) : (
            <Headline style={{ color }}>
              {formatSignedMoney(summary.pnl, symbol, precision)}
            </Headline>
          )}
          <Meta>
            {!pending && (
              <>
                <Figure style={{ color }}>
                  {formatSignedPercent(summary.roi)}
                </Figure>{" "}
                {t("overview.on_margin")}
              </>
            )}
          </Meta>
          <Split>
            <MicroLabel>{t("overview.long_short")}</MicroLabel>
            <Bar role="img" aria-label={t("overview.long_short")}>
              {summary.longMargin + summary.shortMargin > 0 && (
                <>
                  <Segment
                    style={{
                      flexGrow: summary.longMargin,
                      background: tokens.up,
                    }}
                  />
                  <Segment
                    style={{
                      flexGrow: summary.shortMargin,
                      background: tokens.down,
                    }}
                  />
                </>
              )}
            </Bar>
            <Legend>
              <LegendItem>
                <Dot style={{ background: tokens.up }} />
                {t("positions.long")}
                <Figure>{summary.longCount}</Figure>
                <Muted>
                  {formatMoney(summary.longMargin, symbol, precision)} ·{" "}
                  {share(summary.longShare)}
                </Muted>
              </LegendItem>
              <LegendItem>
                <Dot style={{ background: tokens.down }} />
                {t("positions.short")}
                <Figure>{summary.shortCount}</Figure>
                <Muted>
                  {formatMoney(summary.shortMargin, symbol, precision)} ·{" "}
                  {share(
                    summary.longMargin + summary.shortMargin
                      ? 1 - summary.longShare
                      : 0
                  )}
                </Muted>
              </LegendItem>
            </Legend>
          </Split>
        </Lead>
        <Movers>
          <MicroLabel>{t("overview.top_movers")}</MicroLabel>
          {pending ? (
            [0, 1, 2].map((index) => (
              <Skeleton key={index} width="100%" height={22} />
            ))
          ) : summary.movers.length ? (
            summary.movers.map(({ position, pnl, roi }) => {
              const moverColor = directionColor(tokens, pnl);
              return (
                <Mover key={position.key ?? position.symbol}>
                  <IconWithFallback
                    iconType={ICON_TYPES.COIN}
                    coinOrExchangeName={position.symbol}
                    pxIconSize={20}
                  />
                  <MoverName title={position.symbol}>
                    {position.symbol}
                  </MoverName>
                  <Side
                    style={{
                      color: position.is_short ? tokens.down : tokens.up,
                    }}
                  >
                    {position.is_short
                      ? t("positions.short")
                      : t("positions.long")}
                  </Side>
                  <MoverPnl style={{ color: moverColor }}>
                    {formatSignedMoney(pnl, symbol, precision)}
                  </MoverPnl>
                  <MoverRoi style={{ color: moverColor }}>
                    {roi !== undefined ? formatSignedPercent(roi) : ""}
                  </MoverRoi>
                </Mover>
              );
            })
          ) : (
            <Muted>{t("overview.no_open_pnl")}</Muted>
          )}
        </Movers>
      </Body>
    </Wrapper>
  );
};

export default React.memo(NewLookPositionsPnl);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  container-type: inline-size;
`;
const Count = styled.span`
  margin-left: 4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 40px;
  min-height: 0;
  ${NARROW_ZONE} {
    gap: 24px;
  }
  margin-top: 8px;
`;
const Lead = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
const Headline = styled.div`
  ${newLookType.display};
  font-size: 32px;
  line-height: 40px;
`;
const Meta = styled.div`
  min-height: 20px;
  margin-top: 4px;
  ${newLookType.meta};
  font-size: 13px;
  line-height: 20px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Figure = styled.span`
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;
const Split = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;
const MicroLabel = styled.div`
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Bar = styled.div`
  display: flex;
  gap: 2px;
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  background: ${(p) => getNewLookTokens(p.theme).well};
`;
const Segment = styled.span`
  flex-basis: 0;
  min-width: 0;
`;
const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
`;
const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${newLookType.meta};
  font-size: 12.5px;
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
`;
const Muted = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Movers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  > :first-child {
    margin-bottom: 4px;
  }
`;
const Mover = styled.div`
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) 36px minmax(76px, max-content) 56px;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  &:last-child {
    border-bottom: 0;
  }
  ${NARROW_ZONE} {
    grid-template-columns: 20px minmax(0, 1fr) 40px auto;
    > :last-child {
      display: none;
    }
  }
`;
const MoverName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13.5px;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Side = styled.span`
  ${newLookType.meta};
  font-weight: 500;
`;
const MoverPnl = styled.span`
  text-align: right;
  font-size: 13.5px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;
const MoverRoi = styled.span`
  text-align: right;
  ${newLookType.meta};
`;
